import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Flex,
  Button,
  Container,
  FormControl,
  Heading,
  HStack,
  Input,
  Link,
  Stack,
  Text,
  IconButton,
  useToast,
  Divider,
  VStack,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";

import { login } from "../auth";
import { useUser } from "./userRole/UserContext";
import { useCurrency } from "./CurrencyExchange/CurrencyContext";
import { useTranslation } from "./LanguageTranslation/TranslationContext";

import HeroImage from "../assets/images/hero_image.png";
import { FaInstagram, FaLink,FaTiktok,FaYoutube } from "react-icons/fa";
import { EmailIcon } from "@chakra-ui/icons";
import { ReactComponent as LinktreeIcon } from "../assets/icons/linktree_logo_icon.svg";

import "./../styles/styles.css";

const LoginPage = () => {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const { user, setUser } = useUser();
  const history = useHistory();
  const { setLanguage } = useTranslation();
  const { setCurrency } = useCurrency();
  const [isLoginSuccessful, setIsLoginSuccessful] = useState(false);

  function getDeviceDetails() {
    const ua = navigator.userAgent;
    let browser;
    if (/chrome|chromium|crios/i.test(ua)) {
      browser = "Chrome";
    } else if (/firefox|fxios/i.test(ua)) {
      browser = "Firefox";
    } else if (/safari/i.test(ua)) {
      browser = "Safari";
    } else if (/edg/i.test(ua)) {
      browser = "Edge";
    } else if (/opr\//i.test(ua)) {
      browser = "Opera";
    } else if (/msie|trident/i.test(ua)) {
      browser = "IE";
    } else {
      browser = "Unknown Browser";
    }

    let os;
    if (/android/i.test(ua)) {
      os = "Android";
    } else if (/iphone|ipad|ipod/i.test(ua)) {
      os = "iOS";
    } else if (/win/i.test(ua)) {
      os = "Windows";
    } else if (/mac/i.test(ua)) {
      os = "MacOS";
    } else if (/linux/i.test(ua)) {
      os = "Linux";
    } else {
      os = "Unknown OS";
    }

    return {
      full_user_agent: ua,
      simplified_user_agent: `${os} ${browser}`,
      loginDate: new Date().toISOString().slice(0, 10),
    };
  }

  const onSubmit = async (formData) => {
    const deviceDetails = getDeviceDetails();
    const loginPayload = { ...formData, device: deviceDetails };

    try {
      const response = await fetch("/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginPayload),
      });

      const data = await response.json();
      if (response.status === 200) {
        login(data);
        localStorage.setItem("email", data.email);
        localStorage.setItem("username", data.username);
        setLanguage(data.settings.preferred_language);
        setCurrency(data.settings.preferred_currency);

        const { role, organization } = data;
        setUser({ role, organization });
        setIsLoginSuccessful(true);
      } else {
        toast({
          title: "Login failed",
          description: "Your credentials are invalid. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error logging in:", error);
    }
    reset();
  };

  useEffect(() => {
    if (user && isLoginSuccessful) {
      history.push("/home");
      setIsLoginSuccessful(false);
    }
  }, [user, isLoginSuccessful, history]);

  return (
    // IMPORTANT: 'column-reverse' on base makes the second child appear on top on mobile
    <Flex
      direction={{ base: "column-reverse", md: "row" }}
      h="100vh"
      overflow="auto"
    >
      {/* Login Box (will appear at the bottom on mobile) */}
      <Box
        w={{ base: "100%", md: "50%", lg: "40%", xl: "40%" }}
        bg="white"
        p={{ base: 6, md: 10, lg: 20 }}
        pt={{ base: 10, md: 10, lg: 20 }}
        pr={{ base: 4, md: 6, lg: 20 }}
        pl={{ base: 4, md: 6, lg: 20 }}
        alignContent="center"
      >
        <Container>
          <Stack spacing={{ base: 2, md: 4, lg: 4, xl: 6 }}>
            <Heading
              as="h1"
              size="lg"
              textAlign="left"
              mb={2}
              fontFamily="'Manrope', sans-serif"
              fontWeight="bold"
              color="#1A202C"
            >
              Login
            </Heading>
            <Text
              color="#3A4135"
              fontSize={{ base: "md", md: "sm", lg: "md" }}
              fontFamily="'Manrope', sans-serif"
            >
              Welcome! Please fill in your username and password to sign in to
              your account.
            </Text>
            <form onSubmit={handleSubmit(onSubmit)}>
              <FormControl isInvalid={errors.email}>
                <Input
                  id="email"
                  type="email"
                  placeholder="Type your email"
                  {...register("email", { required: "Email is required" })}
                  bg="blackAlpha.100"
                  borderRadius="none"
                  mt={4}
                />
                <Input
                  id="password"
                  type="password"
                  placeholder="Type your password"
                  {...register("password", {
                    required: "Password is required",
                  })}
                  bg="blackAlpha.100"
                  borderRadius="none"
                  mt={4}
                />
                <VStack
                  w="100%"
                  mt={2}
                  spacing={0}
                  alignItems="flex-start"
                  justifyContent="flex-start"
                />
                <Button
                  mt={2}
                  colorScheme="green"
                  type="submit"
                  w="100%"
                  bg="#286328"
                  _hover="none"
                  color="white"
                  fontFamily="'Roboto', sans-serif"
                  fontWeight="medium"
                  borderRadius="full"
                >
                  Login Now
                </Button>
              </FormControl>
            </form>

            <Flex align="center">
              <Divider p={0} mb="0" mt="0" />
              <Text
                textStyle="sm"
                fontFamily="'Manrope', sans-serif"
                fontWeight="medium"
                color="#3A4135"
                mb="0"
                mt="0"
                mr="1"
                ml="1"
                textAlign="center"
              >
                or
              </Text>
              <Divider p={0} mb="0" mt="0" />
            </Flex>

            {/* Social Links */}
            <Text
              textStyle="sm"
              fontFamily="'Manrope', sans-serif"
              fontWeight="medium"
              color="#3A4135"
              mb="0"
              mt="0"
              mr="1"
              ml="1"
              textAlign="center"
            >
              Follow us here
            </Text>
            <HStack spacing={2} mt={0} w="100%" justifyContent="center">
              <IconButton
                as={Link}
                href="https://mail.google.com/mail/?view=cm&fs=1&to=info@nomad360.io"
                aria-label="contact us"
                icon={<EmailIcon />}
                isRound
                bg="blackAlpha.100"
                mt={2}
                target="_blank"
                rel="noopener noreferrer"
              />
              <IconButton
                aria-label="TikTok"
                icon={<FaTiktok />}
                isRound
                bg="blackAlpha.100"
                mt={2}
                as="a"
                href="https://www.tiktok.com/@nomad360.io"
                target="_blank"
                rel="noopener noreferrer"
              />
              <IconButton
                aria-label="Instagram"
                icon={<FaInstagram />}
                isRound
                bg="blackAlpha.100"
                mt={2}
                as="a"
                href="https://www.instagram.com/nomad360.io/"
                target="_blank"
                rel="noopener noreferrer"
              />
              <IconButton
                aria-label="YouTube"
                icon={<FaYoutube />}
                isRound
                bg="blackAlpha.100"
                mt={2}
                as="a"
                href="https://youtube.com/@nomad360io"
                target="_blank"
                rel="noopener noreferrer"
              />
            </HStack>
          </Stack>
        </Container>

        <Box
          alignContent="center"
          justifyContent="center"
          p={5}
          pb={2}
          pt={{ base: 10, md: 2, lg: 6 }}
        >
          <HStack alignContent="center" justifyContent="center">
            <Link
              fontSize="12"
              href="/policies/terms-of-service"
              fontFamily="'Manrope', sans-serif"
              fontWeight="medium"
              color="#1A202C"
            >
              Terms of Service
            </Link>
            <Divider
              alignSelf="center"
              orientation="vertical"
              height="12px"
              borderColor="black"
              borderWidth="0.5px"
            />
            <Link
              fontSize="12"
              href="/policies/privacy-policy"
              fontFamily="'Manrope', sans-serif"
              fontWeight="medium"
              color="#1A202C"
            >
              Privacy Policy
            </Link>
          </HStack>
        </Box>
      </Box>

      {/* Image Box (will appear at the top on mobile) */}
      <Box
        w={{ base: "100%", md: "50%", lg: "60%", xl: "60%" }}
        h={{ base: "auto", md: "100vh" }}
        bgImage={HeroImage}
        bgPosition="center"
        bgRepeat="no-repeat"
        bgSize="cover"
        display="flex"
        flexDirection="column"
        justifyContent="top"
        alignItems="center"
        color="white"
        p={{ base: 4, md: 4, lg: 10 }}
      >
        <VStack
          w="auto"
          maxW={{ base: "100%", md: "100%", lg: "80%" }}
          mt={{ base: 0, md: 6, lg: 20 }}
          alignContent="center"
          textAlign="center"
          justifyContent="center"
          borderRadius="xl"
          bg="rgba(255, 255, 255, 0.4)"
          p={{ base: 4, md: 4, lg: 4 }}
        >
          <Heading
            color="#1A202C"
            fontSize={{ base: "1xl", md: "2xl", lg: "3xl" }}
            fontFamily="'ui', sans-serif"
            textAlign="left"
            mb={0}
          >
            Get Ready for Your Travel Adventure!
          </Heading>
          <Text
            color="#1A202C"
            fontSize={{ base: "xs", md: "md", lg: "lg" }}
            fontFamily="'ui', sans-serif"
            fontWeight="bold"
            textAlign="left"
            mb={0}
          >
            We’re putting the finishing touches on our AI Travel Planner.
            <br />
            Stay tuned for a quick launch!
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
};

export default LoginPage;
